import { useTranslation } from "react-i18next"

import { BarChart3, Plus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { SettingsService } from "@/services/settings-service"
import { useUseCasesStore } from "@/stores/use-cases-store"

import { DashboardCharts } from "./dashboard-charts"
import { DashboardFilters } from "./dashboard-filters"
import { DashboardKpis } from "./dashboard-kpis"
import { DashboardTable } from "./dashboard-table"
import { SiteComparisonTab } from "./site-comparison/site-comparison-tab"
import { useUseCaseFilters } from "../../hooks/use-use-case-filters"
import { useAllUseCasesAdmin, useUseCases } from "../../hooks/use-use-cases"

export function DashboardTab() {
  const { t } = useTranslation()
  const { isAdminView, setIsAdminView, setActiveTab } = useUseCasesStore()

  const settingsService = SettingsService.getInstance()
  const isUseCaseAdmin = settingsService.settings.user_policy.is_usecase_admin

  const { data: useCases, isLoading } = useUseCases()
  const { data: adminUseCases, isLoading: isAdminLoading } = useAllUseCasesAdmin(
    isAdminView && isUseCaseAdmin
  )

  const activeUseCases = isAdminView && isUseCaseAdmin ? adminUseCases : useCases
  const activeLoading = isAdminView && isUseCaseAdmin ? isAdminLoading : isLoading

  const { filteredUseCases, allSites, filters, updateFilter, clearFilters } =
    useUseCaseFilters(activeUseCases)

  if (activeLoading) {
    return (
      <div className="space-y-6">
        <div className="flex gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 flex-1" />
          ))}
        </div>
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
        <Skeleton className="h-96" />
      </div>
    )
  }

  if (!activeUseCases || activeUseCases.length === 0) {
    return (
      <div className="space-y-6">
        {isUseCaseAdmin && (
          <div className="flex items-center gap-2">
            <Switch checked={isAdminView} id="admin-view-toggle" onCheckedChange={setIsAdminView} />
            <Label htmlFor="admin-view-toggle">{t("useCases.dashboard.adminViewLabel")}</Label>
          </div>
        )}
        <div className="flex h-64 flex-col items-center justify-center gap-4">
          <div className="bg-muted flex size-16 items-center justify-center rounded-full">
            <BarChart3 className="text-muted-foreground size-8" />
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold">{t("useCases.dashboard.emptyTitle")}</h3>
            <p className="text-muted-foreground mt-1 text-sm">
              {t("useCases.dashboard.emptyDescription")}
            </p>
          </div>
          <Button onClick={() => setActiveTab("factsheet")}>
            <Plus className="mr-1 size-4" />
            {t("useCases.dashboard.createFirst")}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {isUseCaseAdmin && (
        <div className="flex items-center gap-2">
          <Switch checked={isAdminView} id="admin-view-toggle" onCheckedChange={setIsAdminView} />
          <Label htmlFor="admin-view-toggle">Admin View - All Use Cases</Label>
        </div>
      )}

      <DashboardFilters
        allSites={allSites}
        clearFilters={clearFilters}
        filters={filters}
        updateFilter={updateFilter}
      />

      <DashboardKpis useCases={filteredUseCases} />

      <Tabs defaultValue="overview">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="overview">{t("useCases.dashboard.tabs.overview")}</TabsTrigger>
          <TabsTrigger value="siteComparison">
            {t("useCases.dashboard.tabs.siteComparison")}
          </TabsTrigger>
        </TabsList>

        <TabsContent className="mt-6" value="overview">
          <div className="space-y-6">
            <DashboardTable showOwnerColumn={isAdminView} useCases={filteredUseCases} />
            <DashboardCharts useCases={filteredUseCases} />
          </div>
        </TabsContent>

        <TabsContent value="siteComparison">
          <SiteComparisonTab useCases={filteredUseCases} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
